import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Button } from '@mui/material';
import Alert from '@mui/material/Alert';
import { ParametersContext } from 'components/ParametersGuard';
import ApplicationDecisionModal from 'components/applications/ApplicationDecisionModal';
import ApplicationQuestionsFormSection from 'components/applications/ApplicationQuestionsFormSection';
import Spinner from 'components/shared/Spinner';
import { useSnackbar } from 'notistack';
import { useFormContext } from 'react-hook-form';
import { useHistory, useParams } from 'react-router';
import ApplicationsService, { ApplicationSubmitResponseData } from 'services/api/ApplicationsService';
import routes from 'store/configs/Routes';
import ApplicationAnswer, {
  ApplicationAnswerData,
  ApplicationFileAnswer,
  ApplicationUploadedFile,
  SublistAnswerData,
} from 'store/types/ApplicationAnswer';
import { ApplicationContentSectionView, ApplicationContentView } from 'store/types/ApplicationContent';
import ApplicationQuestion from 'store/types/ApplicationQuestion';
import {
  getAnswersData,
  getApplicationStatusAlert,
  getApplicationTitle,
  getFormattedApplicationStatus,
  getFormattedSections,
  getQuestionId,
} from 'util/Application';
import ApplicationStatus from 'store/enums/ApplicationStatus';
import ApplicationPageLayout from 'components/applications/ApplicationPageLayout';
import useUnsavedChangesPrompt from 'hooks/useUnsavedChangesPrompt';
import { getUnsavedChangesPromptMessage } from 'util/Form';
import ApplicationConfirmationModal from 'components/applications/ApplicationConfirmationModal';
import { defaultSnackbarErrorProps } from 'util/Layout';

import commonStyles from 'styles/common.module.scss';
import pageStyles from '../ApplicationPage.module.scss';

interface EditApplicationPageViewProps {
  data: ApplicationContentView;
  isPrivate?: boolean;
  refetch: () => void;
}

const EditApplicationPageView: React.FunctionComponent<EditApplicationPageViewProps> = ({
  data,
  isPrivate = false,
  refetch,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { handleSubmit, getValues } = useFormContext();
  const { applicationId = '' } = useParams<{ applicationId: string }>();
  const {
    claims: { claimsApplicationTypeId, disputesApplicationTypeId },
  } = useContext(ParametersContext);
  const { removeUnsavedChangesPrompt, prompt } = useUnsavedChangesPrompt(getUnsavedChangesPromptMessage());
  const [decisionModalOpen, setDecisionModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [showValidationError, setShowValidationError] = useState<boolean>(false);
  const [submitResult, setSubmitResult] = useState<ApplicationSubmitResponseData | undefined>(undefined);
  const [sections, setSections] = useState<ApplicationContentSectionView[]>([]);
  const questionsList: ApplicationQuestion[] = useMemo(() => {
    let result: ApplicationQuestion[] = [];

    if (data) {
      (data.sections || []).forEach(({ questions = [] }) => {
        result = result.concat(questions);
      });
    }
    return result;
  }, [data]);

  const hasContent: boolean = useMemo(() => !!(data && data.sections && data.sections.length), [data]);

  const handleDecisionModalOpen = useCallback(() => {
    setDecisionModalOpen(true);
  }, []);

  const handleDecisionModalClose = useCallback(() => {
    setDecisionModalOpen(false);
  }, []);

  useEffect(() => {
    if (data) {
      setSections(getFormattedSections(data.sections));
    }
  }, [data]);

  const submitButtonDisabled: boolean = useMemo(() => !hasContent || loading, [hasContent, loading]);

  const scrollToTop = useCallback(() => {
    const sectionElement: HTMLElement | null = document.getElementById('top');

    if (sectionElement) {
      sectionElement.scrollIntoView();
    }
  }, []);

  const showValidationAlerts = (applicationData: ApplicationSubmitResponseData) => {
    applicationData.messages.forEach((item) => {
      const alertElement = document.getElementById(`${item.questionId}-alert`);
      if (alertElement) {
        alertElement.style.display = 'block';
      }
    });
  };

  const clearValidationAlerts = () => {
    const elements = document.querySelectorAll(`[data-alert='true']`);
    elements.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.style.display = 'none';
      }
    });
  };

  const isBlobFile = (x: unknown): x is File => typeof File !== 'undefined' && x instanceof File;

  const uploadFiles = useCallback(
    (request, answersData: ApplicationAnswer[], files: ApplicationFileAnswer, sublistFiles?: ApplicationFileAnswer) => {
      const result: ApplicationAnswerData[] = answersData;
      const uploadPromiseArray: Promise<ApplicationUploadedFile>[] = [];

      if (files) {
        Object.entries(files).forEach(([questionId, files]) => {
          if (files?.length) {
            files.forEach((file: File) => {
              if (isBlobFile(file)) {
                uploadPromiseArray.push(ApplicationsService.uploadFile(applicationId, questionId, file));
              }
            });
          }
        });
      }

      if (sublistFiles) {
        Object.entries(sublistFiles).forEach(([referenceKey, sublistAnswer]) => {
          const questionId = sublistAnswer?.questionId;
          const sublistQuestionId = sublistAnswer?.sublistQuestionId;
          const files = sublistAnswer?.files;

          if (files?.length) {
            files.forEach((file: File) => {
              if (isBlobFile(file)) {
                uploadPromiseArray.push(
                  ApplicationsService.uploadFile(applicationId, questionId, file, sublistQuestionId, referenceKey)
                );
              }
            });
          }
        });
      }

      if (!uploadPromiseArray.length) {
        request(answersData);
        return;
      }

      Promise.all(uploadPromiseArray)
        .then((data: ApplicationUploadedFile[]) => {
          data.forEach((item: ApplicationUploadedFile) => {
            const fileQuestionId: string = getQuestionId(item.questionId);
            const answerIndex = result.findIndex(({ questionId }) => questionId === fileQuestionId);
            if (answerIndex >= 0) {
              if (item.referenceKey && item.sublistQuestionId) {
                const sublistAnswerSet: any = result[answerIndex]?.sublistAnswers || [];
                let setIndex = -1;
                let sublistAnswerIndex = -1;
                sublistAnswerSet.forEach((answerSet: any, outerIndex: number) =>
                  answerSet.forEach(({ sublistQuestionId, referenceKey }: SublistAnswerData, innerIndex: number) => {
                    if (sublistQuestionId === item.sublistQuestionId && referenceKey === item.referenceKey) {
                      setIndex = outerIndex;
                      sublistAnswerIndex = innerIndex;
                    }
                  })
                );

                if (setIndex >= 0 && sublistAnswerIndex >= 0) {
                  const existing = sublistAnswerSet[setIndex][sublistAnswerIndex] || [];
                  if (existing) existing.files = [...(existing.files || []), { id: item.fileId }];
                }
              } else result[answerIndex].files = [...(result[answerIndex].files || []), { id: item.fileId }];
            }
          });

          request(result);
        })
        .catch((errorMessage: string) => {
          setLoading(false);
          enqueueSnackbar(errorMessage, defaultSnackbarErrorProps);
        });
    },
    [applicationId, enqueueSnackbar]
  );

  const handleFormSave = useCallback(() => {
    setLoading(true);
    const formValues = getValues();
    const answersData: ApplicationAnswer[] = getAnswersData(formValues, questionsList);
    const files: ApplicationFileAnswer = formValues['files'];
    const sublistFiles: ApplicationFileAnswer = formValues['sublistFiles'];

    uploadFiles(
      (data: ApplicationAnswerData[]) =>
        ApplicationsService.updateApplicationContent(applicationId, data)
          .then(() => {
            setLoading(false);
            removeUnsavedChangesPrompt();
            enqueueSnackbar('Application successfully saved', { variant: 'success' });
            refetch();
          })
          .catch((error: string) => {
            setLoading(false);
            enqueueSnackbar(error, defaultSnackbarErrorProps);
          }),
      answersData,
      files,
      sublistFiles
    );
  }, [applicationId, enqueueSnackbar, getValues, uploadFiles, questionsList, removeUnsavedChangesPrompt, refetch]);

  const handleFormInvalid = useCallback(() => {
    scrollToTop();
    setShowValidationError(true);
  }, [scrollToTop]);

  const handleFormSubmit = useCallback(
    (formValues: any) => {
      setLoading(true);
      const answersData: ApplicationAnswer[] = getAnswersData(formValues, questionsList);
      const files: ApplicationFileAnswer = formValues['files'];
      const sublistFiles: ApplicationFileAnswer = formValues['sublistFiles'];
      clearValidationAlerts();

      uploadFiles(
        (data: ApplicationAnswerData[]) =>
          ApplicationsService.submitApplicationContent(applicationId, data)
            .then((result: ApplicationSubmitResponseData) => {
              const { isOk, messages = [] } = result;
              setLoading(false);
              setSubmitResult(result);
              if (isOk) {
                removeUnsavedChangesPrompt();
                setShowValidationError(false);
                enqueueSnackbar('Application successfully submitted', { variant: 'success' });
              } else if (messages.length) {
                setShowValidationError(true);
                scrollToTop();
                showValidationAlerts(result);
              }
            })
            .catch((error: string) => {
              setLoading(false);
              enqueueSnackbar(error, defaultSnackbarErrorProps);
            }),
        answersData,
        files,
        sublistFiles
      );
    },
    [applicationId, enqueueSnackbar, questionsList, removeUnsavedChangesPrompt, scrollToTop, uploadFiles]
  );

  const applicationsUrl = useMemo(
    () =>
      (claimsApplicationTypeId && data.typeId === claimsApplicationTypeId) ||
      (disputesApplicationTypeId && data.typeId === disputesApplicationTypeId)
        ? routes.claims
        : data.createdById && data.applicantId !== data.createdById
        ? routes.companyApplications
        : routes.applications,
    [claimsApplicationTypeId, data.applicantId, data.createdById, data.typeId, disputesApplicationTypeId]
  );

  const handleCancel = useCallback(() => {
    history.push(applicationsUrl);
  }, [applicationsUrl, history]);

  return (
    <>
      <ApplicationPageLayout
        data={data}
        footerProps={
          isPrivate
            ? {
                onSubmit: handleFormSave,
                submitButtonName: 'Save',
                submitButtonDisabled,
              }
            : {
                onSubmit: handleSubmit(handleFormSubmit, handleFormInvalid),
                submitButtonName: 'Submit',
                submitButtonDisabled,
                children: (
                  <>
                    <Button
                      color={'secondary'}
                      variant={'outlined'}
                      onClick={handleCancel}
                      className={pageStyles.cancelButton}
                      disabled={loading}
                    >
                      {'Go Back'}
                    </Button>
                    {data.decisionDate && (
                      <Button color={'secondary'} variant={'outlined'} onClick={handleDecisionModalOpen}>
                        {'View Decision'}
                      </Button>
                    )}
                    <Button
                      color={'secondary'}
                      variant={'outlined'}
                      onClick={handleFormSave}
                      disabled={!hasContent || loading}
                      className={pageStyles.saveButton}
                    >
                      {'Save'}
                    </Button>
                  </>
                ),
              }
        }
      >
        <Spinner loading={loading}>
          {data && (
            <>
              <div id={'top'} />
              <div className={pageStyles.statusAlert}>{getApplicationStatusAlert(data)}</div>
              <div className={pageStyles.questionsWrapper}>
                {data.title && <h1 className={commonStyles.pageTitle}>{getApplicationTitle(data)}</h1>}
                <Alert severity={'error'} className={commonStyles.alert} hidden={!showValidationError}>
                  All required fields must be completed before the application can be submitted.
                </Alert>
                {hasContent ? (
                  <ApplicationQuestionsFormSection sections={sections} />
                ) : (
                  <Alert severity={'error'} className={commonStyles.alert}>
                    {'No content found'}
                  </Alert>
                )}
              </div>
            </>
          )}
        </Spinner>
      </ApplicationPageLayout>
      {submitResult && (
        <ApplicationConfirmationModal
          open={submitResult.isOk}
          showPaymentLink={getFormattedApplicationStatus(submitResult.status) === ApplicationStatus.PendingPayment}
          applicationsUrl={applicationsUrl}
        />
      )}
      {prompt}
      {data.decisionDate && (
        <ApplicationDecisionModal onClose={handleDecisionModalClose} open={decisionModalOpen} application={data} />
      )}
    </>
  );
};
export default EditApplicationPageView;
